import React, { FocusEventHandler, useEffect, useState } from "react";
import { FormControl, InputLabel, Box, makeStyles, useTheme, SvgIcon } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateRangeIcon from "@material-ui/icons/DateRange";
import { Theme } from "../.themes";
import DateFnsUtils from "@date-io/date-fns";

export type DateTimePickerGroupProps = {
  label?: {
    id?: string;
    title?: string;
  };
  input?: {
    onChange?: Function;
    id?: string;
    value?: Date;
    helperText?: string;
    hasError?: boolean;
    isDisabled?: boolean;
    format?: string;
  };
  forwardedRef?: React.RefObject<HTMLDivElement>;
};

const useStyles = makeStyles(() => ({
  box: {
    display: "flex",
    alignItems: "center",
  },
}));

export const DateTimePickerGroupBase: React.FC<DateTimePickerGroupProps> = (props: DateTimePickerGroupProps) => {
  const theme: Theme = useTheme();
  const css = useStyles(theme);

  const label = props.label;
  const input = props.input;
  const [onFocusInputValue, setOnFocusInputValue] = useState<string | null>(null);

  let labelElement;
  if (label && label?.title) {
    labelElement = (
      <InputLabel id={label?.id} shrink={false}>
        {label?.title}
      </InputLabel>
    );
  }

  let labelArea;
  if (labelElement) {
    labelArea = <Box className={css.box}>{labelElement}</Box>;
  }
  function isValidDate(dateString?: string) {
    if (!dateString) {
      return false;
    }

    const parts = dateString.split("-");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month === 0 || month > 12) {
      return false;
    }

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
      monthLength[1] = 29;
    }

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
  }

  const hasError = props.input?.hasError;

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector(".Mui-error>input") as HTMLInputElement;
      input?.focus();
    }, 1);
    return () => clearTimeout(timer);
  }, [hasError]);

  function handleOnAccept(newValue: any, currentValue?: Date, eventHandler?: Function) {
    if (newValue) {
      if (eventHandler && newValue?.getTime() !== currentValue?.getTime()) {
        eventHandler(newValue);
      }
    }
  }

  function handleOnBlur(newValue: any, onFocusInputValue: string | null, currentValue?: Date, eventHandler?: Function) {
    //we only want to make API call when input value has changed
    if (eventHandler && onFocusInputValue !== newValue) {
      const testDate = isValidDate(newValue);
      if (testDate) {
        const date = newValue.split("-");
        eventHandler(new Date(date[0], date[1] - 1, date[2]));
      } else {
        eventHandler(null);
      }
    }
  }

  const handleOnFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = event => {
    setOnFocusInputValue(event.target.value);
  };

  const calendar = (theme.icons?.dateTimeAltIcon && <SvgIcon>{theme.icons?.dateTimeAltIcon}</SvgIcon>) ??
    (theme.icons?.dateTimeIcon && <SvgIcon>{theme.icons?.dateTimeIcon}</SvgIcon>) ?? <DateRangeIcon />;

  const refusePattern = () => {
    return /[^0-9]+/gi;
  };

  return (
    <Box marginY={1} id={"ws-datePicker-" + props.input?.id}>
      <FormControl fullWidth>
        {labelArea}
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id={input?.id}
            keyboardIcon={calendar}
            inputVariant='outlined'
            inputRef={props.forwardedRef}
            format={input?.format ?? "yyyy-MM-dd"}
            disablePast={true}
            mask='____-__-__'
            placeholder='ÅÅÅÅ-MM-DD'
            refuse={refusePattern()}
            disabled={input?.isDisabled}
            error={input?.hasError}
            helperText={input?.helperText}
            value={input?.value ?? null}
            onChange={() => undefined}
            onFocus={handleOnFocus}
            onAccept={date => handleOnAccept(date, input?.value, props.input?.onChange)}
            onBlur={date => {
              handleOnBlur(date.target.value, onFocusInputValue, input?.value, props.input?.onChange);
            }}
            KeyboardButtonProps={{
              "aria-label": "Change date " + label?.title,
            }}
          />
        </MuiPickersUtilsProvider>
      </FormControl>
    </Box>
  );
};
